import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { usePnLData } from '../../hooks/useMarketData';
import api from '../../services/api';
import './PnLTimeChart.css';

const MINUTE_INTERVAL = 60;

const alignToMinute = (timestamp) => Math.floor(timestamp / MINUTE_INTERVAL) * MINUTE_INTERVAL;

const getMarketHours = () => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(9, 15, 0, 0);
    const end = new Date(now);
    end.setHours(15, 30, 0, 0);
    return {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000),
    };
};

const isWithinMarketHours = (timestamp) => {
    const { start, end } = getMarketHours();
    return timestamp >= start && timestamp <= end;
};

const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
    });
};

const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
    }) + ' ' + now.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
    });
};

const PnLTimeChart = () => {
    const chartRef = useRef(null);
    const [chartData, setChartData] = useState({
        times: [],
        pnl: [],
    });
    const dataMapRef = useRef({
        pnl: new Map(),
    });
    const [loading, setLoading] = useState(false);

    // WebSocket hook for real-time PnL updates
    const { data: wsPnLData } = usePnLData();

    // Fetch historical PnL data from 9:15 AM
    const loadHistoricalData = useCallback(async () => {
        setLoading(true);
        try {
            const now = new Date();
            const marketOpen = new Date(now);
            marketOpen.setHours(9, 15, 0, 0);
            const startTimestamp = Math.floor(marketOpen.getTime() / 1000);
            const endTimestamp = Math.floor(now.getTime() / 1000);

            const response = await api.get(
                `/pnl/history/total?start=${startTimestamp}&end=${endTimestamp}&limit=25000&granularity=1m`
            );

            if (response.data?.data && response.data.data.length > 0) {
                // Reset map
                dataMapRef.current = { pnl: new Map() };

                response.data.data.forEach(point => {
                    const time = point.timestamp;
                    if (!isWithinMarketHours(time)) return;
                    const minuteTime = alignToMinute(time);
                    dataMapRef.current.pnl.set(minuteTime, point.value);
                });

                updateChartData();
            }
        } catch (err) {
            console.error("Error fetching PnL history:", err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // updateChartData is defined after, uses refs

    // Load historical data on mount
    useEffect(() => {
        loadHistoricalData();
    }, [loadHistoricalData]);

    // Handle real-time WebSocket updates
    useEffect(() => {
        if (!wsPnLData) return;

        const timestamp = wsPnLData.updated_at || Math.floor(Date.now() / 1000);
        const pnlValue = parseFloat(wsPnLData.total_pnl) || 0;

        if (!isWithinMarketHours(timestamp)) return;

        const minuteTime = alignToMinute(timestamp);
        dataMapRef.current.pnl.set(minuteTime, pnlValue);

        updateChartData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wsPnLData]); // updateChartData is defined after, uses refs

    // Helper to convert map to arrays and update state
    const updateChartData = useCallback(() => {
        const now = Math.floor(Date.now() / 1000);
        const times = [...dataMapRef.current.pnl.keys()]
            .filter(t => t <= now)  // Only show times up to current time
            .sort((a, b) => a - b);

        setChartData({
            times,
            pnl: times.map(t => dataMapRef.current.pnl.get(t) || null),
        });
    }, []);

    // Compute max/min PnL from chart data
    const { maxPnL, minPnL } = useMemo(() => {
        const validPnL = chartData.pnl.filter(v => v !== null);
        if (validPnL.length === 0) return { maxPnL: 0, minPnL: 0 };
        return {
            maxPnL: Math.max(...validPnL),
            minPnL: Math.min(...validPnL)
        };
    }, [chartData.pnl]);

    const getChartOption = useCallback(() => {
        const { times, pnl } = chartData;

        // Get last PnL value for color
        const lastPnL = pnl.filter(v => v !== null).slice(-1)[0] || 0;
        const lineColor = lastPnL >= 0 ? '#22C55E' : '#EF4444';
        const areaColorStart = lastPnL >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';
        const areaColorEnd = lastPnL >= 0 ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.05)';

        return {
            animation: false,
            title: {
                text: 'PnL Timeline',
                subtext: formatDateTime(),
                left: 'center',
                top: 5,
                textStyle: { fontSize: 14, fontWeight: 500 },
                subtextStyle: { fontSize: 11, color: '#666' },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', animation: false },
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderColor: '#ddd',
                borderWidth: 1,
                textStyle: { fontSize: 11 },
                formatter: (params) => {
                    if (!params || params.length === 0) return '';
                    const value = params[0].value;
                    const color = value >= 0 ? '#22C55E' : '#EF4444';
                    return `<strong>${params[0].axisValue}</strong><br/>
                            <span style="color:${color}">PnL: ${value?.toFixed(2)}</span>`;
                },
            },
            legend: {
                data: ['PnL'],
                bottom: 5,
                textStyle: { fontSize: 10 },
                icon: 'line',
                itemWidth: 20,
                itemHeight: 2,
            },
            grid: {
                left: 55,
                right: 55,
                top: 55,
                bottom: 75,
            },
            xAxis: {
                type: 'category',
                data: times.map(formatTime),
                axisLabel: { fontSize: 9, color: '#666' },
                axisLine: { lineStyle: { color: '#ddd' } },
                axisTick: { lineStyle: { color: '#ddd' } },
                boundaryGap: false,
            },
            yAxis: {
                type: 'value',
                name: 'PnL',
                nameTextStyle: { fontSize: 9, color: lineColor },
                position: 'left',
                axisLabel: { fontSize: 9, color: '#666' },
                axisLine: { show: true, lineStyle: { color: '#ddd' } },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
                scale: true,
            },
            dataZoom: [
                {
                    type: 'slider',
                    show: true,
                    xAxisIndex: [0],
                    bottom: 28,
                    height: 18,
                    borderColor: '#ddd',
                    fillerColor: 'rgba(59,130,246,0.1)',
                    handleStyle: { color: '#3B82F6' },
                    textStyle: { fontSize: 9 },
                },
                {
                    type: 'inside',
                    xAxisIndex: [0],
                },
            ],
            series: [
                {
                    name: 'PnL',
                    type: 'line',
                    data: pnl,
                    symbol: 'none',
                    lineStyle: { width: 2, color: lineColor },
                    itemStyle: { color: lineColor },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: areaColorStart },
                                { offset: 1, color: areaColorEnd },
                            ],
                        },
                    },
                    endLabel: {
                        show: true,
                        formatter: (params) => params.value?.toFixed(2) || '',
                        color: '#fff',
                        backgroundColor: lineColor,
                        padding: [2, 4],
                        borderRadius: 2,
                        fontSize: 9,
                    },
                },
            ],
        };
    }, [chartData]);

    return (
        <div className="pnl-chart-container">
            <div className="pnl-stats-bar">
                <span className="pnl-stat">
                    Hi: <span className={maxPnL >= 0 ? 'positive' : 'negative'}>
                        {maxPnL >= 0 ? '+' : ''}{maxPnL.toFixed(0)}
                    </span>
                </span>
                <span className="pnl-stat">
                    Lo: <span className={minPnL >= 0 ? 'positive' : 'negative'}>
                        {minPnL >= 0 ? '+' : ''}{minPnL.toFixed(0)}
                    </span>
                </span>
            </div>
            {loading && <div className="pnl-chart-loading">Loading...</div>}
            <ReactECharts
                ref={chartRef}
                option={getChartOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={false}
                lazyUpdate={true}
            />
        </div>
    );
};

export default PnLTimeChart;
